import React from "react";
import { TextField, Tooltip, Box } from "@mui/material";
import { useFormikContext } from "formik";

export interface GenericMonthDayFieldProps {
  name: string;
  label: string;
  disabled?: boolean;
  tooltip?: string;
  required?: boolean;
  size?: "small" | "medium";
}

const GenericMonthDayField: React.FC<GenericMonthDayFieldProps> = ({
  name,
  label,
  disabled = false,
  tooltip,
  required = false,
  size = "small",
}) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext<any>();

  // Helper function to get nested values safely
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Helper function to safely convert error to string
  const safeErrorToString = (error: any): string => {
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error !== null) {
      return Object.values(error).join(', ');
    }
    return '';
  };

  // Format input as MM-DD
  const formatMonthDay = (value: string): string => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Format based on length
    if (digits.length === 0) return '';
    if (digits.length <= 2) return digits;
    
    // Auto-insert hyphen after month
    const month = digits.substring(0, 2);
    const day = digits.substring(2, 4);
    return `${month}-${day}`;
  };

  const fieldValue = getNestedValue(values, name);
  const fieldError = getNestedValue(errors, name);
  const fieldTouched = getNestedValue(touched, name);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const formattedValue = formatMonthDay(inputValue);
    setFieldValue(name, formattedValue || null);
  };

  const handleBlur = () => {
    setFieldTouched(name, true);
  };

  // Convert month number to name
  const getMonthName = (month: string): string => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const monthIndex = parseInt(month, 10) - 1;
    return months[monthIndex] || month;
  };

  // Add ordinal suffix to day
  const getDayWithSuffix = (day: string): string => {
    const dayNum = parseInt(day, 10);
    if (isNaN(dayNum)) return day;
    
    const suffixes = ["th", "st", "nd", "rd"];
    const value = dayNum % 100;
    const suffix = suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0];
    return `${dayNum}${suffix}`;
  };

  // Generate helper text
  const getHelperText = (): string => {
    if (fieldTouched && fieldError) {
      return safeErrorToString(fieldError);
    }
    if (fieldValue && fieldValue.includes('-')) {
      const [month, day] = fieldValue.split('-');
      const monthName = getMonthName(month);
      const dayWithSuffix = getDayWithSuffix(day);
      return `${monthName}, ${dayWithSuffix}`;
    }
    return "Format: MM-DD";
  };

  const monthDayField = (
    <TextField
      fullWidth
      size={size}
      name={name}
      label={label}
      value={fieldValue || ''}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder="MM-DD"
      required={required}
      inputProps={{
        maxLength: 5, // MM-DD = 5 characters
      }}
      error={fieldTouched && Boolean(fieldError)}
      helperText={getHelperText()}
      sx={{ mb: 2 }}
    />
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="top">
      <Box>{monthDayField}</Box>
    </Tooltip>
  ) : (
    monthDayField
  );
};

export default GenericMonthDayField;
